import React, { useState, useEffect } from 'react';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { ChevronLeft, Home, Menu, CheckCircle, AlertCircle, BookOpen, Calculator, ChevronRight, Lightbulb, HelpCircle, Sparkles } from 'lucide-react';
import { GradientText, InsightCard, Button, IconTile } from '../../components/ui/index.tsx';
import { ACAT_LEVEL_DATA } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import ProgramOverviewSection from './ProgramOverviewSection.tsx';
import { ChspComparisonModal } from '../../components/care/EntryLevelSupportExplainer.tsx';
import { MyAgedCareApplyLink } from '../../components/branding/index.tsx';
import ContributionsSection from './ContributionsSection.tsx';
import StrategiesSection from './StrategiesSection.tsx';
import ServiceBuilderSection from './ServiceBuilderSection.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

type SupportAtHomeSection = 'overview' | 'contributions' | 'strategies' | 'builder';

/**
 * THE ONE ORDER this module is walked in. Tabs, Previous/Next and every label read from it.
 *
 * NEED BEFORE MONEY — the Service Builder comes before Optimise (owner direction, 2026-08-27).
 * Both orders "work", because the two are only weakly coupled and asymmetrically so: optimising
 * moves assets and income, which sets the contribution RATES, and those come from the means
 * assessment rather than from which services are chosen. The Service Builder sets the BUDGET those
 * rates apply to. Out-of-pocket is budget x rate.
 *
 * So optimising first never gave wrong ADVICE — whichever strategy wins on the illustrative package
 * still wins on the real one. It gave provisional DOLLARS at the moment a client is most
 * impressionable, and nobody re-checks "your share falls to $3,929 a year" after building their
 * services. It is also the right advice order: what care someone needs is a clinical question, not
 * a financial one.
 *
 * ONE ARRAY, because there were two. The tabs listed the order and the bottom Next/Back buttons
 * carried their own hardcoded copy of it, so reordering the tabs left the button still reading
 * "Next: Optimise Your Plan" on the Contributions screen while it navigated to the builder
 * (owner-caught immediately, 2026-08-27). A second source of the same sequence will always drift;
 * deriving prev/next by index cannot.
 */
const SECTION_FLOW: {
    id: SupportAtHomeSection;
    label: string;
    icon: typeof BookOpen;
    /**
     * This section draws its own Back/Next footer, so the page must not add a second one.
     *
     * The Service Builder's footer is contextual — it sits with "2 services selected" and the save
     * prompt — which is why it keeps its own rather than being flattened into the generic pair.
     * Before this flag the page rendered Previous/Next underneath it and the screen ended with two
     * Back buttons and two "Next: Optimise Your Plan" buttons (owner-caught, 2026-08-27).
     */
    ownsFooterNav?: true;
}[] = [
    { id: 'overview', label: 'Program Overview', icon: BookOpen },
    { id: 'contributions', label: 'Your Contributions', icon: Calculator },
    { id: 'builder', label: 'Service Builder', icon: Menu, ownsFooterNav: true },
    { id: 'strategies', label: 'Optimise Your Plan', icon: Lightbulb },
];

/** True when the section on screen supplies its own footer navigation. */
const sectionOwnsFooterNav = (id: SupportAtHomeSection): boolean =>
    SECTION_FLOW.some((s) => s.id === id && s.ownsFooterNav === true);

/** The section before/after `id` in SECTION_FLOW, or null at either end. */
const stepFrom = (id: SupportAtHomeSection, delta: 1 | -1): { id: SupportAtHomeSection; label: string } | null => {
    const at = SECTION_FLOW.findIndex((s) => s.id === id);
    return SECTION_FLOW[at + delta] ?? null;
};
type StrategyMode = 'manual' | 'suggestion' | '';

export interface SupportAtHomePageProps {
    setActivePage: (page: string) => void;
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    /**
     * Called with each generated Digital SOA (the strategy plus its printable snapshot) so it lands on
     * the server's advice register like aged care's (owner, 1 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const SupportAtHomePage: React.FC<SupportAtHomePageProps> = ({ setActivePage, factFind, setFactFind, onSoaGenerated }) => {
    /*
     * WHEN SUPPORT SERVICES START, owned here because two sections need it: the Service Builder ASKS
     * (it is a property of the service plan) and Optimise READS (it is the date the Suggestion Engine
     * projects every candidate from). One input, one value — asking twice is how two screens come to
     * disagree about the same fact.
     */
    const [supportStartDate, setSupportStartDate] = useState<string>('');
    const [currentSection, setCurrentSection] = useState<SupportAtHomeSection>('overview'); // 'overview', 'contributions', 'strategies', 'builder'
    // '' until the user chooses - the mode choice is the FIRST act, matching the aged care
    // module (owner-caught difference, 2026-08-25: a new client landed with Manual Control's
    // levers already spread out).
    const [strategyMode, setStrategyMode] = useState<StrategyMode>('');
    
    // Clear any leftover restore strategy on mount if not explicitly reopening
    useEffect(() => {
        // Check if we're explicitly reopening a strategy from Dashboard
        const reopenStrategy = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionReopenHomeCareStrategy') : null;
        if (!reopenStrategy) {
            // No explicit reopen - clear any leftover restore strategy to prevent restoring unsaved strategies
            if (typeof window !== 'undefined') {
                const leftover = sessionStorage.getItem('careCompanionRestoreHomeCareStrategy');
                if (leftover) {
                    try {
                        const strategy = JSON.parse(leftover);
                        // Only clear if it's not a saved strategy (no id means it's leftover unsaved state)
                        if (!strategy.id) {
                            sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                        }
                    } catch {
                        // Clear corrupted data
                        sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                    }
                }
            }
        }
    }, []);
    
    // Check for reopened home care strategy
    useEffect(() => {
        try {
            const stored = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionReopenHomeCareStrategy') : null;
            if (stored) {
                const strategy = JSON.parse(stored);
                // Clear the stored strategy so it doesn't reopen again
                sessionStorage.removeItem('careCompanionReopenHomeCareStrategy');
                
                // Check if this is a service plan (has inputs.services) or a strategy from Optimise Your Plan
                // Service plans have inputs.services, strategies have inputs.gifting/funeralBonds
                // Priority: Check for service plan first (more specific), then strategy
                const hasServicePlan = strategy.inputs?.services !== undefined && 
                                      strategy.inputs?.services !== null &&
                                      Object.keys(strategy.inputs.services || {}).length > 0;
                const hasStrategyInputs = (strategy.inputs?.gifting !== undefined && strategy.inputs?.gifting !== null) || 
                                         (strategy.inputs?.funeralBonds !== undefined && strategy.inputs?.funeralBonds !== null);
                
                
                if (hasServicePlan && !hasStrategyInputs) {
                    // Navigate to Service Builder section to restore the service plan
                    setCurrentSection('builder');
                    // Store it for ServiceBuilderSection to restore
                    sessionStorage.setItem('careCompanionRestoreServicePlan', JSON.stringify(strategy));
                } else if (hasStrategyInputs) {
                    // Navigate to strategies section to restore strategy state
                    setCurrentSection('strategies');
                    // Store it for StrategiesSection to access
                    sessionStorage.setItem('careCompanionRestoreHomeCareStrategy', JSON.stringify(strategy));
                } else {
                    // Default to strategies if unclear (most home_care items are strategies from Optimise Your Plan)
                    setCurrentSection('strategies');
                    sessionStorage.setItem('careCompanionRestoreHomeCareStrategy', JSON.stringify(strategy));
                }
            }
        } catch (error) {
            console.error('Error loading reopened home care strategy:', error);
        }
    }, []);
    
    // Scroll to top when section changes
    useEffect(() => {
        scrollAppToTop();
    }, [currentSection]);
    
    // Check if this is a couple
    const isPartnered = factFind?.adviceFor === 'couple';
    
    // Get client's Support at Home level from ACAT assessment
    const getClientLevel = (): { label: string; budget: number; description: string } | null => {
        const clientAcat = factFind?.careNeeds?.clientAcat;
        if (clientAcat?.status === 'yes' && clientAcat?.homeCareLevel) {
            return ACAT_LEVEL_DATA[clientAcat.homeCareLevel] || null;
        }
        return null;
    };

    // Get partner's Support at Home level from ACAT assessment
    const getPartnerLevel = (): { label: string; budget: number; description: string } | null => {
        const partnerAcat = factFind?.careNeeds?.partnerAcat;
        if (partnerAcat?.status === 'yes' && partnerAcat?.homeCareLevel) {
            return ACAT_LEVEL_DATA[partnerAcat.homeCareLevel] || null;
        }
        return null;
    };

    const clientLevel = getClientLevel();
    const partnerLevel = getPartnerLevel();
    const [selectedLevel, setSelectedLevel] = useState<number | null>(clientLevel ? clientLevel.budget : null);
    const [selectedPartnerLevel, setSelectedPartnerLevel] = useState<number | null>(partnerLevel ? partnerLevel.budget : null);
    const [compareOpen, setCompareOpen] = useState(false);

    /*
     * A GUESS IS NOT A CLASSIFICATION (owner-caught, 2026-08-28).
     *
     * This screen used to greet an unassessed client with "we couldn't find your classification —
     * please select your recommended level", and the moment they picked one it appeared under
     * "Your Support at Home Classification" beside a green tick, with a budget to the cent. Every
     * contribution figure downstream was then calculated from a number the client had guessed off a
     * dropdown, and `clientLevel || { budget: selectedLevel }` handed it to ContributionsSection in
     * the same slot as a real assessment — so nothing downstream could tell the two apart.
     *
     * The exploration itself is worth keeping: someone waiting on an assessment reasonably wants to
     * know what a level would cost. What had to go is calling it their classification. `exploring`
     * is that distinction, and it now travels with the figure to every tab that shows one.
     */
    const clientExploring = !clientLevel && selectedLevel != null;
    const partnerExploring = !partnerLevel && selectedPartnerLevel != null;
    const exploring = clientExploring || partnerExploring;
    const clientName = factFind?.client?.firstName || 'You';
    const clientVerb = factFind?.client?.firstName ? 'hasn’t' : 'haven’t';

    const formatCurrency = (value: number | null | undefined): string => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
    };

    /*
     * THE OPTIMISE SECTION IS THE ONLY TWO-PANE ONE, and it needs the benchmark's width to lay its
     * panes out the way Aged Care does: 1520px, giving a ~587px working pane and a ~391px plan
     * summary at the owner's 100% zoom (a ~1330px viewport). Capping it at 1152px squeezed both.
     *
     * The other sections stay at 1152px on purpose — they are single columns of reading, and prose
     * set 1520px wide is harder to read, not easier.
     */
    return (
        <div className={currentSection === 'strategies' ? 'max-w-[1520px] mx-auto' : 'max-w-6xl mx-auto'}>
            <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => setActivePage('Dashboard')} className="mb-4 -ml-2">
                Back to Dashboard
            </Button>
            
            <div className="flex items-center gap-4 mb-2">
                <IconTile icon={Home} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
                <h1 className="text-4xl font-bold text-gray-900">
                    <GradientText>
                        {currentSection === 'contributions' ? 'Support at Home - Your Contributions' :
                         currentSection === 'strategies' ? 'Optimise Your Support at Home Plan' :
                         currentSection === 'builder' ? 'Build Your Service Plan' :
                         'Understanding Support at Home'}
                    </GradientText>
                </h1>
            </div>
            <p className="text-lg text-gray-600 mb-8">
                {currentSection === 'contributions' ? 'Understand your estimated contributions based on your financial position and care needs.' :
                 currentSection === 'strategies' ? 'Explore strategies to minimize your Support at Home contributions and maximize your outcomes.' :
                 currentSection === 'builder' ? 'Select and build your personalized Support at Home service plan within your budget.' :
                 "Australia's new aged care program designed to help you live independently at home. Learn about your budget, contributions, and how to optimise your care plan."}
            </p>

            {/* Not assessed: say so honestly, then offer the what-if as a what-if. */}
            {currentSection === 'overview' && !clientLevel && (
                <div className="space-y-4 mb-8">
                    <InsightCard tone="caution" heading={`${clientName} ${clientVerb} been assessed yet`}>
                        <p className="mb-3">
                            Support at Home costs depend on the level you&apos;re assessed at, so until that
                            happens we can&apos;t tell you what you&apos;d pay.{' '}
                            <strong>An assessment may also find that entry-level support (CHSP) meets your
                            needs</strong> — that program has no income or assets test, so there&apos;s nothing
                            for us to model.{' '}
                            <button
                                type="button"
                                onClick={() => setCompareOpen(true)}
                                className="font-semibold text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700"
                            >
                                How do the two compare?
                            </button>
                        </p>
                        <MyAgedCareApplyLink status="no" />
                    </InsightCard>

                    <InsightCard tone="neutral" heading="Want to see the numbers anyway? Try a level" icon={Sparkles}>
                        <p className="mb-3">
                            Pick a level to explore what it <em>would</em> cost. This is a what-if, not your
                            classification — we&apos;ll label it that way everywhere it appears.
                        </p>
                        <select
                            value={selectedLevel || ''}
                            onChange={(e) => setSelectedLevel(e.target.value ? parseFloat(e.target.value) : null)}
                            aria-label="Explore a Support at Home level"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                        >
                            <option value="">Choose a level to explore…</option>
                            {Object.entries(ACAT_LEVEL_DATA).filter(([, value]) => !value.transitional).map(([key, value]) => (
                                <option key={key} value={value.budget}>
                                    {value.label} - {formatCurrency(value.budget)} per year
                                </option>
                            ))}
                        </select>
                    </InsightCard>
                </div>
            )}

            <ChspComparisonModal open={compareOpen} onClose={() => setCompareOpen(false)} />

            {/* Classification Display - Only show on overview tab */}
            {currentSection === 'overview' && (clientLevel || selectedLevel || partnerLevel || selectedPartnerLevel) && (
                <div className="space-y-4 mb-8">
                    {/*
                      * The tile, the heading and the footnote all change with provenance. An assessed
                      * level keeps the tick and the definite heading; an explored one gets a question
                      * mark, a dashed edge, a conditional heading and an amber chip — so the reader
                      * cannot mistake one for the other at a glance, which is exactly what happened
                      * before (owner-caught, 2026-08-28).
                      */}
                    {(clientLevel || selectedLevel) && (
                    <div className={`bg-white/60 backdrop-blur-xl rounded-2xl shadow-lg shadow-gray-200/50 p-6 border ${clientLevel ? 'border-white' : 'border-dashed border-slate-300'}`}>
                        <div className="flex items-start gap-4">
                            <IconTile icon={clientLevel ? CheckCircle : HelpCircle} tone={clientLevel ? 'brand' : 'slate'} variant="solid" size="lg" />
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2.5 mb-2">
                                    <h4 className="text-lg font-bold text-gray-800">
                                        {clientExploring
                                            ? `If ${isPartnered ? (factFind?.client?.firstName || 'they') : 'you'} ${isPartnered ? 'were' : 'were'} assessed at this level`
                                            : `${isPartnered ? `${factFind?.client?.firstName || 'Client'}'s` : 'Your'} Support at Home Classification`}
                                    </h4>
                                    {clientExploring && (
                                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold tracking-wide text-amber-800">
                                            EXPLORING — NOT ASSESSED
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                                        {clientLevel?.label || `Support at Home - Level ${Object.entries(ACAT_LEVEL_DATA).find(([_, v]) => v.budget === selectedLevel)?.[0]?.replace('sah_level_', '') || ''}`}
                                    </span>
                                    <span className="text-2xl font-bold text-gray-900">
                                        {formatCurrency(clientLevel?.budget || selectedLevel)}
                                    </span>
                                    <span className="text-sm text-gray-600">per year</span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    {clientExploring
                                        ? 'The budget at this level. The real level could be higher or lower — only an assessment decides it.'
                                        : 'This budget is paid quarterly and managed by Services Australia.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    )}

                    {/* Partner Classification (if coupled) */}
                    {isPartnered && (
                        <div className={`bg-white/60 backdrop-blur-xl rounded-2xl shadow-lg shadow-gray-200/50 p-6 border ${partnerLevel ? 'border-white' : 'border-dashed border-slate-300'}`}>
                            <div className="flex items-start gap-4">
                                <IconTile icon={partnerLevel ? CheckCircle : HelpCircle} tone={partnerLevel ? 'brand' : 'slate'} variant="solid" size="lg" />
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                                        <h4 className="text-lg font-bold text-gray-800">
                                            {partnerExploring
                                                ? `If ${factFind?.partner?.firstName || 'they'} were assessed at this level`
                                                : partnerLevel
                                                    ? `${factFind?.partner?.firstName || 'Partner'}'s Support at Home Classification`
                                                    : `${factFind?.partner?.firstName || 'Your partner'} hasn’t been assessed yet`}
                                        </h4>
                                        {partnerExploring && (
                                            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold tracking-wide text-amber-800">
                                                EXPLORING — NOT ASSESSED
                                            </span>
                                        )}
                                    </div>
                                    {partnerLevel || selectedPartnerLevel ? (
                                        <>
                                            <div className="flex flex-wrap items-center gap-3 mb-2">
                                                <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                                                    {partnerLevel?.label || `Support at Home - Level ${Object.entries(ACAT_LEVEL_DATA).find(([_, v]) => v.budget === selectedPartnerLevel)?.[0]?.replace('sah_level_', '') || ''}`}
                                                </span>
                                                <span className="text-2xl font-bold text-gray-900">
                                                    {formatCurrency(partnerLevel?.budget || selectedPartnerLevel)}
                                                </span>
                                                <span className="text-sm text-gray-600">per year</span>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-2">
                                                {partnerExploring
                                                    ? 'The budget at this level. The real level could be higher or lower — only an assessment decides it.'
                                                    : 'This budget is paid quarterly and managed by Services Australia.'}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm text-amber-700 mb-2 flex items-center gap-1.5">
                                                <AlertCircle size={15} className="flex-shrink-0" />
                                                You can explore what a level would cost:
                                            </p>
                                            <select
                                                value={selectedPartnerLevel || ''}
                                                onChange={(e) => setSelectedPartnerLevel(e.target.value ? parseFloat(e.target.value) : null)}
                                                aria-label={`Explore a Support at Home level for ${factFind?.partner?.firstName || 'partner'}`}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                                            >
                                                <option value="">Choose a level to explore…</option>
                                                {Object.entries(ACAT_LEVEL_DATA).map(([key, value]) => (
                                                    <option key={key} value={value.budget}>
                                                        {value.label} - {formatCurrency(value.budget)} per year
                                                    </option>
                                                ))}
                                            </select>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Navigation Tabs */}
            <div className="flex items-center gap-4 mb-8 border-b border-gray-300">
                {/*
                  * NEED BEFORE MONEY — the Service Builder comes before Optimise (owner direction,
                  * 2026-08-27).
                  *
                  * Both orders "work": the two are only weakly coupled, and asymmetrically.
                  * Optimising moves assets and income, which sets the contribution RATES — and the
                  * rates come from the means assessment, not from which services are chosen. The
                  * Service Builder sets the BUDGET those rates apply to. Out-of-pocket is
                  * budget x rate.
                  *
                  * So optimising first never produced wrong ADVICE: whichever strategy wins on the
                  * illustrative package still wins on the real one. What it produced was provisional
                  * DOLLARS at the moment a client is most impressionable — and nobody re-checks
                  * "your share falls to $3,929 a year" after building their services.
                  *
                  * It is also the right advice order: what care someone needs is a clinical
                  * question, not a financial one, and choosing services after seeing the
                  * optimisation invites trimming them because they look expensive rather than
                  * because they are not needed.
                  *
                  * Still not a gate. Anyone can jump straight to Optimise, which prices against the
                  * illustrative package and says so.
                  */}
                {SECTION_FLOW.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setCurrentSection(tab.id)}
                        className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all border-b-2 ${
                            currentSection === tab.id
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <div className={`w-5 h-5 flex items-center justify-center ${currentSection === tab.id ? 'text-blue-600' : 'text-gray-500'}`}>
                            <tab.icon size={20} strokeWidth={currentSection === tab.id ? 2.5 : 2} />
                        </div>
                        {tab.label}
                        {/*
                          * The label follows the figure. Every tab after the overview prices against
                          * the explored level, and a client can arrive on one directly — from a
                          * bookmark, or by clicking through — without ever seeing the overview's
                          * "not assessed" card. Without this, those screens read as settled.
                          */}
                        {exploring && tab.id !== 'overview' && (
                            <span className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-amber-800">
                                WHAT-IF
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Section Content */}
            {currentSection === 'overview' && (
                <ProgramOverviewSection
                    selectedLevel={selectedLevel}
                    clientLevel={clientLevel}
                    onNext={() => setCurrentSection('contributions')}
                    awaitingAssessment={!clientLevel && !partnerLevel}
                    onNavigate={setActivePage}
                />
            )}
            {/* Contributions now hands on to the BUILDER, and the builder to Optimise — the same
                order as the tabs above, so Next/Back walk the sequence a reader is shown. */}
            {currentSection === 'contributions' && <ContributionsSection factFind={factFind} setFactFind={setFactFind} clientLevel={clientLevel || { budget: selectedLevel }} onNext={() => setCurrentSection('builder')} onBack={() => setCurrentSection('overview')} />}
            {currentSection === 'builder' && <ServiceBuilderSection factFind={factFind} setFactFind={setFactFind} onBack={() => setCurrentSection('contributions')} onNext={() => setCurrentSection('strategies')} setCurrentSection={(section: string) => setCurrentSection(section as SupportAtHomeSection)} setActivePage={setActivePage} supportStartDate={supportStartDate} setSupportStartDate={setSupportStartDate} />}
            {currentSection === 'strategies' && (
                <StrategiesSection
                    onSoaGenerated={onSoaGenerated}
                    factFind={factFind}
                    setFactFind={setFactFind}
                    onNext={() => setActivePage('Dashboard')}
                    onBack={() => setCurrentSection('builder')}
                    strategyMode={strategyMode}
                    setStrategyMode={(mode: string) => setStrategyMode(mode as StrategyMode)}
                    onNavigateToBuilder={() => setCurrentSection('builder')}
                    onNavigateToDashboard={() => setActivePage('Dashboard')}
                    supportStartDate={supportStartDate}
                />
            )}

            {/* Bottom Navigation Buttons — suppressed where the section draws its own. */}
            {!sectionOwnsFooterNav(currentSection) && (
            <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
                {/* Derived from SECTION_FLOW, never restated: the label and the destination are read
                    from the same array, so a button can no longer name one section and open another. */}
                <Button
                    variant="ghost"
                    icon={ChevronLeft}
                    onClick={() => {
                        const previous = stepFrom(currentSection, -1);
                        if (previous) setCurrentSection(previous.id);
                        else setActivePage('Dashboard');
                    }}
                >
                    {stepFrom(currentSection, -1) ? 'Previous' : 'Back to Dashboard'}
                </Button>

                {stepFrom(currentSection, 1) && (
                    <Button
                        variant="brand"
                        size="lg"
                        iconRight={ChevronRight}
                        onClick={() => {
                            const next = stepFrom(currentSection, 1);
                            if (next) setCurrentSection(next.id);
                        }}
                    >
                        Next: {stepFrom(currentSection, 1)?.label}
                    </Button>
                )}
            </div>
            )}
        </div>
    );
};


export default SupportAtHomePage;
